import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastAutoHideDuration } from 'Src/consts/duration';
import { useCheck } from 'Src/feature/check/useCheck';
import { useCheckHeader } from 'Src/feature/check/useCheckHeader';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useDOMEventListener } from 'Src/feature/dom-event/useDOMEventListener';
import { useEmployee } from 'Src/feature/employee/useEmployee';
import { useFloorPlan } from 'Src/feature/floor-plan/useFloorPlan';
import { useOloConfig } from 'Src/feature/olo-config/useOloConfig';
import { usePrinter } from 'Src/feature/printer/usePrinter';
import { useToast } from 'Src/feature/toasts/useToast';
import useRoute from 'Src/routes/useRoute';

import { AuthRequestToastNotification } from './AuthRequestToastNotification';
import { LANPrintFailureToastNotification } from './LANPrintFailureToastNotification';
import { PrintFailureToastNotification } from './PrintFailureToastNotification';
import { S2PCheckPaidToastNotification } from './S2PCheckPaidToastNotification';
import * as types from './types';
import { useIsMyNotification } from './useIsMyNotification';
import { NotificationUI, useNotification } from './useNotification';

const sectionId = 'NotificationListener';
const NotificationListener: FC = () => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const toast = useToast();
  const { navigate } = useRoute();
  const { add: addNotification, remove: removeNotification, notificationList } = useNotification();
  const { isMyNotification } = useIsMyNotification();
  const checkInvalidate = useCheck.query.invalidate();
  const checkHeaderInvalidate = useCheckHeader.query.invalidate();
  const oloConfigInvalidate = useOloConfig.invalidate.get();
  const checkGetOne = useCheck.mutate.getOne(); // need to use a mutation to be able to call on demand
  const updatePrinterStatus = usePrinter.mutate.updateStatus();

  // event driven sync listeners
  useEmployee.sync.shift();
  useEmployee.sync.employee();
  useFloorPlan.sync.table();

  useDOMEventListener('notify.AuthorizationRequestCreated', (evt) => {
    const newNotification = new NotificationUI<types.AuthorizationRequestCreated>({
      type: 'authorizationRequest',
      id: evt.detail.id?.toString() as string,
      time: Date.now(),
      data: evt.detail,
    });

    addNotification(newNotification);

    if (isMyNotification(newNotification)) {
      toast.make({
        // sharing the same id, so we only show the last toast
        id: `${sectionId}.auth.request`,
        autoHideDuration: ToastAutoHideDuration.Notification,
        type: 'custom',
        render: (t) => (
          <AuthRequestToastNotification
            notification={newNotification}
            onAction={() => toast.store?.hide(t)}
          />
        )
      }).show();
    }
  });

  useDOMEventListener('notify.AuthorizationRequestAnswered', (evt) => {
    removeNotification(evt.detail.id?.toString() as string);
  });

  useDOMEventListener('notify.CheckPrintJobResult', (evt) => {
    // task 1: invalidate the check cache
    // NOTE: CheckPrintJobResult messages from the server indicate that the check has been updated because
    //       a print job status has changed. In that case we need to update the check.
    //       BUT.... we don't need to update the check header since print job statuses are not in the check header.
    //       Therefore we don't do a fetch of the check and a check "sync" ... we instead just invalidate the check data.
    checkInvalidate.getOne(evt.detail.checkId);

    // task 2: make a notification if there was a print error
    //         everything below here is part of task 2
    // we only want to handle error states
    if (!evt.detail.errStr) return;

    const newNotification = new NotificationUI<types.CheckPrintJobFailure>({
      type: 'printFailure',
      id: evt.detail.id?.toString(),
      time: Date.now(),
      data: evt.detail,
    });

    if (isMyNotification(newNotification)) {
      addNotification(newNotification);
      toast.make({
        // sharing the same id, so we only show the last toast
        id: `${sectionId}.print.failure`,
        autoHideDuration: ToastAutoHideDuration.Notification,
        type: 'custom',
        render: (t) => (
          <PrintFailureToastNotification
            notification={newNotification}
            onAction={() => toast.store?.hide(t)}
          // TODO: Why doesn't this PrintFailureToastNotification have onNavigate wired up? 
          // It uses a translation key that expects `<primary>` and `<navigate>` components.
          />
        )
      }).show();
    }
  });

  // notify.TerminalLANPrintJobError is slightly different in that it comes from a background worker mechanism
  // instead of API backend.
  useDOMEventListener('notify.TerminalLANPrintJobError', (evt) => {
    const newNotification = new NotificationUI<types.LANPrintJobFailure>({
      type: 'lanPrintFailure',
      id: evt.detail.jobId,
      time: Date.now(),
      data: evt.detail,
    });

    removeNotification(evt.detail.jobId);
    addNotification(newNotification);
    toast.make({
      // sharing the same id, so we only show the last toast
      id: `${sectionId}.lanPrintJob.failure`,
      autoHideDuration: ToastAutoHideDuration.Notification,
      type: 'custom',
      render: (t) => (
        <LANPrintFailureToastNotification
          notification={newNotification}
          onAction={() => toast.store?.hide(t)}
          onNavigate={navigate}
        />
      )
    }).show();
  });

  useDOMEventListener('notify.CheckPrintJobActionTaken', (evt) => {
    evt.detail?.jobUids?.forEach((jobId) => removeNotification(jobId));
  });

  useDOMEventListener('notify.PrinterInfoChanged', (evt) => {
    if (!evt.detail.printer?.id) return;
    // always remove the current status
    removeNotification(evt.detail.printer.id.toString());
    // only put a status into notifications if that status is not good
    if (evt.detail.printer.status !== 'Ready') addNotification(new NotificationUI<types.PrinterInfoChanged>({
      type: 'printerStatus',
      id: evt.detail.printer.id.toString(),
      time: Date.now(),
      data: evt.detail,
    }));
  });

  useDOMEventListener('notify.LANPrinterStatusChanged', (evt) => {
    // LAN printer status gets fired from worker instead of backend API events,
    // so we need to push the status update to the backend.
    updatePrinterStatus.mutate({
      printerId: evt.detail.id,
      status: evt.detail.status,
    }, {
      onError: (error) => {
        log.error(`failed to update printer status`, {
          printerId: evt.detail.id,
          status: evt.detail.status,
          error,
        });
      }
    });
  });

  useDOMEventListener('notify.S2P_CheckPaid', (evt) => {
    // task 1: invalidate the check cache
    // NOTE: S2P_CheckPaid messages from the server indicate that the check has been updated because
    //       the check has been paid in full and was closed. In that case we need to update the check.
    //       We also need to update the check header because, while payments do not live on the
    //       check header, the overall check status does.
    checkInvalidate.getOne(evt.detail.check.id);
    checkHeaderInvalidate.all();

    // task 2: make a notification
    const newNotification = new NotificationUI<types.S2PCheckPaid>({
      type: 's2pCheckPaid',
      id: evt.detail.check?.id?.toString(),
      time: Date.now(),
      data: evt.detail,
    });
    addNotification(newNotification);
    if (isMyNotification(newNotification)) {
      toast.make({
        id: `${sectionId}.check.paid.${evt.detail.check?.id ?? ''}`,
        autoHideDuration: ToastAutoHideDuration.Notification,
        type: 'custom',
        render: () => (
          <S2PCheckPaidToastNotification notification={newNotification} />
        ),
      }).show();
    }
  });

  useDOMEventListener('notify.CheckPaymentGoodFaithTransaction', (evt) => {
    const newNotification = new NotificationUI<types.CheckPaymentGoodFaithTransaction>({
      type: 'safTransaction',
      id: evt.detail.payment.id,
      time: Date.now(),
      data: evt.detail,
    });
    if (!notificationList.find((n) => n.type === 'safTransaction' && (n as NotificationUI<types.CheckPaymentGoodFaithTransaction>).data?.checkId === newNotification.data.checkId)) {
      addNotification(newNotification);
    }
    if (isMyNotification(newNotification)) {
      toast.showAndAutoHide.error({ title: t('notification.safTransaction.transactionProcessedOffline'), id: `${sectionId}.payment.safTransaction.${evt.detail.payment.id ?? ''}` });
    }
  });

  useDOMEventListener('notify.CheckPaymentGoodFaithRerun', (evt) => {
    const matchingNotification = notificationList.find((n) => n.type === 'safTransaction' && (n as NotificationUI<types.CheckPaymentGoodFaithTransaction>).data?.checkId === evt.detail.checkId);
    switch (evt.detail.status) {
      case 'Declined':
        checkGetOne.mutate(evt.detail.checkId, {
          onSuccess: (data) => {
            if (matchingNotification) removeNotification(matchingNotification.id);
            toast.showAndAutoHide.error({ title: t('payment.labels.paymentDeclinedOnCheck', { checkLabel: data?.label ?? '' }), id: `${sectionId}.payment.safTransaction.retry.${evt.detail.checkId ?? ''}` });
          },
        });
        break;
      case 'Success':
        if (matchingNotification) removeNotification(matchingNotification.id);
        break;
      case 'GoodFaith':
        toast.showAndAutoHide.error({ title: t('notification.safTransaction.rerunOffline') });
        break;
      case 'Error':
        toast.showAndAutoHide.error({ title: t('notification.safTransaction.rerunError') });
        break;
      case 'VoidedGoodFaith':
        checkGetOne.mutate(evt.detail.checkId, {
          onSuccess: (data) => {
            if (matchingNotification) removeNotification(matchingNotification.id);
            toast.showAndAutoHide.error({ title: t('notification.safTransaction.rerunVoided', { checkLabel: data?.label ?? '' }), id: `${sectionId}.payment.safTransaction.retry.${evt.detail.checkId ?? ''}` });
          },
        });
        break;
      default: break;
    }
  });

  useDOMEventListener('notify.EntityCUD.OloConfig.Updated', oloConfigInvalidate);
  useDOMEventListener('notify.EntityCUD.OloConfig.Created', oloConfigInvalidate);

  return null;
};

export { NotificationListener, sectionId };
